import { useEffect, useState } from 'react'
import { AlertTriangle, CreditCard, HardDrive, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { NoticeItem } from '@/features/notices/NoticeItem'
import { usePermissions } from '@/hooks/usePermissions'
import { useBillingLockStore } from '@/stores/useBillingLockStore'
import { billingQueryKeys, useBillingStatus } from '../hooks/useBilling'
import { billingNotices, type BillingNotice } from './bannerCopy'

const ICONS: Record<BillingNotice['id'], typeof Lock> = {
  'read-only': Lock,
  'past-due': AlertTriangle,
  trial: CreditCard,
  storage: HardDrive,
}

/**
 * Billing notices in the app shell: trial days left, read-only, failed payment
 * and storage. Everything comes from GET /billing/status. A 402 anywhere in the
 * app re-reads it, so the banner explains a refused write straight away.
 */
export function BillingBanner(): React.ReactElement | null {
  const { can } = usePermissions()
  const canManage = can('settings', 'update')
  const { data } = useBillingStatus()
  const queryClient = useQueryClient()
  const refusedAt = useBillingLockStore((s) => s.refusedAt)
  // In memory only: a closed notice comes back on reload, and the urgent ones
  // cannot be closed at all.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (refusedAt === null) return
    // A prefix key, so the billing page's own query refreshes too.
    void queryClient.invalidateQueries({ queryKey: billingQueryKeys.all })
  }, [refusedAt, queryClient])

  const notices = billingNotices(data, canManage).filter((n) => !dismissed.has(n.id))
  if (notices.length === 0) return null

  return (
    <div className="space-y-2 px-6 pt-2">
      {notices.map((notice) => {
        const Icon = ICONS[notice.id]
        return (
          <NoticeItem
            key={notice.id}
            tone={notice.tone}
            icon={(className) => <Icon className={className} aria-hidden />}
            title={notice.title}
            footer={
              notice.cta ? (
                <Link
                  to={notice.cta.to}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {notice.cta.label}
                </Link>
              ) : undefined
            }
            onDismiss={
              notice.dismissible
                ? () => setDismissed((previous) => new Set(previous).add(notice.id))
                : undefined
            }
          >
            {notice.body}
          </NoticeItem>
        )
      })}
    </div>
  )
}
